import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isQuotaWarningThresholdReached } from '../../../../shared-types/src/index.js';
import type {
  AdminGroupView,
  AdminUserView,
  AllocationOverview,
  CreditLimitView,
  DesktopModelView,
  EffectiveCreditLimitsView,
  QuotaProfileView,
  QuotaScope,
  UpsertCreditLimitRequest
} from '../../api.js';
import { CREDITS_PATTERN, toCredits, toMillis } from '../../components/AllocationPanel.js';
import { TabStrip } from '../../components/TabStrip.js';
import { describeError } from '../../errors.js';
import { useTabState } from '../../hooks/index.js';
import type { AdminHttpClient } from '../../http.js';
import type { ToastMessage } from '../../types.js';
import {
  CreditLimitForm,
  type CreditLimitFormState,
  type LimitSubjectKind,
  type LimitSubjectOption
} from './CreditLimitForm.js';
import { CreditLimitTable, subjectLabel } from './CreditLimitTable.js';
import { EffectiveLimitsPanel } from './EffectiveLimitsPanel.js';
import { MemberDefaultPanel } from './MemberDefaultPanel.js';
import { QuotaAssignmentReviewPanel } from './QuotaAssignmentReviewPanel.js';
import { QuotaProfilesPanel } from './QuotaProfilesPanel.js';

// Seven panels in one column made the tail (profiles, effective limits)
// invisible without scrolling. Grouped by task, in the order limits get set up:
// cap spend, decide what a new member starts with, package repeats as profiles,
// then check what actually applies to someone. The credit-limit form and table
// must share a tab — the table's edit button fills that form.
const TABS = [
  { id: 'limits', label: 'creditLimits.tabLimits' },
  { id: 'memberDefault', label: 'creditLimits.tabMemberDefault' },
  { id: 'profiles', label: 'creditLimits.tabProfiles' },
  { id: 'preview', label: 'creditLimits.tabPreview' },
] as const;
type TabId = (typeof TABS)[number]['id'];
const TAB_IDS = TABS.map((item) => item.id);

const EMPTY_FORM: CreditLimitFormState = {
  // Plan §22 defaults: a group, weekly, all models, blocking.
  subjectKind: 'group',
  subjectId: '',
  amount: '',
  cycle: 'weekly',
  applyToTenant: false,
  catalogModelId: '',
  overageBehavior: 'deny'
};

function scopeOf(state: CreditLimitFormState): QuotaScope {
  if (state.applyToTenant) return { kind: 'tenant' };
  return state.subjectKind === 'group'
    ? { kind: 'group', groupId: state.subjectId }
    : { kind: 'user', userId: state.subjectId };
}

export function UsageLimitsScreen({
  http,
  onToast
}: {
  http: AdminHttpClient;
  onToast: (toast: ToastMessage) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [tab, setTab] = useTabState<TabId>('veluga.admin.usageLimitsTab', 'limits', TAB_IDS);

  const [pool, setPool] = useState<AllocationOverview | undefined>();
  const [groups, setGroups] = useState<AdminGroupView[]>([]);
  const [users, setUsers] = useState<AdminUserView[]>([]);
  const [models, setModels] = useState<DesktopModelView[]>([]);
  const [limits, setLimits] = useState<CreditLimitView[]>([]);
  const [advancedPolicyCount, setAdvancedPolicyCount] = useState(0);
  const [form, setForm] = useState<CreditLimitFormState>(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  /** The last save failed with credit_rate_missing — offer the warn-only path. */
  const [rateMissing, setRateMissing] = useState(false);

  /** The assignment table renders profile names, so both panels read one list
   *  and a change on either side bumps the revision the other watches. */
  const [profiles, setProfiles] = useState<QuotaProfileView[]>([]);
  const [profileRevision, setProfileRevision] = useState(0);

  const [previewKind, setPreviewKind] = useState<LimitSubjectKind>('user');
  const [previewId, setPreviewId] = useState('');
  const [previewModel, setPreviewModel] = useState('');
  const [preview, setPreview] = useState<EffectiveCreditLimitsView | undefined>();

  const groupOptions: LimitSubjectOption[] = useMemo(
    () => groups.map((group) => ({ id: group.id, label: group.name })),
    [groups]
  );
  const userOptions: LimitSubjectOption[] = useMemo(
    () => users.map((user) => ({ id: user.id, label: user.name || user.email })),
    [users]
  );
  const modelOptions: LimitSubjectOption[] = useMemo(
    () => models.map((model) => ({ id: model.id, label: model.displayName || model.id })),
    [models]
  );
  const formSubjects = form.subjectKind === 'group' ? groupOptions : userOptions;
  const previewSubjects = previewKind === 'group' ? groupOptions : userOptions;

  const nameOf = useCallback(
    (kind: 'group' | 'user', id: string) =>
      (kind === 'group' ? groupOptions : userOptions).find((option) => option.id === id)?.label ??
      id,
    [groupOptions, userOptions]
  );

  const reloadLimits = useCallback(async () => {
    const result = await http.listCreditLimits();
    setLimits(result.items);
    setAdvancedPolicyCount(result.advancedPolicyCount);
  }, [http]);

  const reloadProfiles = useCallback(async () => {
    setProfiles((await http.listQuotaProfiles()).items);
  }, [http]);

  useEffect(() => {
    void (async () => {
      try {
        const [poolResult, groupList, userList, modelList] = await Promise.all([
          http.tenantGetOwnAllocation(),
          http.listGroups(),
          http.listUsers(),
          http.listCatalog()
        ]);
        setPool(poolResult);
        setGroups(groupList);
        setUsers(userList);
        setModels(modelList);
        await Promise.all([reloadLimits(), reloadProfiles()]);
      } catch (error) {
        onToast({ tone: 'error', message: describeError(error, t) });
      }
    })();
  }, [http, onToast, reloadLimits, reloadProfiles, t]);

  const refreshPreview = useCallback(async () => {
    if (!previewId) {
      setPreview(undefined);
      return;
    }
    try {
      setPreview(
        await http.previewEffectiveCreditLimits({
          subject:
            previewKind === 'user'
              ? { kind: 'user', userId: previewId }
              : { kind: 'group', groupId: previewId },
          ...(previewModel ? { catalogModelId: previewModel } : {})
        })
      );
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, t) });
    }
  }, [http, onToast, previewId, previewKind, previewModel, t]);

  useEffect(() => {
    void refreshPreview();
  }, [refreshPreview]);

  const save = useCallback(
    async (overrideBehavior?: 'warn') => {
      const raw = form.amount.trim();
      if (!CREDITS_PATTERN.test(raw)) {
        onToast({ tone: 'error', message: t('creditLimits.amountInvalid') });
        return;
      }
      const input: UpsertCreditLimitRequest = {
        scope: scopeOf(form),
        cycle: form.cycle,
        creditMillis: toMillis(raw),
        overageBehavior: overrideBehavior ?? form.overageBehavior,
        ...(form.catalogModelId ? { catalogModelId: form.catalogModelId } : {}),
        ...(form.replaceId ? { replaceId: form.replaceId } : {})
      };
      setBusy(true);
      try {
        await http.upsertCreditLimit(input);
        // Keep the form values on failure (below) but clear them on success.
        setForm(EMPTY_FORM);
        setRateMissing(false);
        onToast({ tone: 'success', message: t('creditLimits.saved') });
        await Promise.all([reloadLimits(), refreshPreview()]);
      } catch (error) {
        const code = (error as { code?: string } | undefined)?.code;
        // ~28% of catalog models have no rate, so this is a common path: offer
        // the warn-only save as one click instead of making the operator find
        // the advanced section.
        setRateMissing(code === 'credit_rate_missing');
        onToast({ tone: 'error', message: describeError(error, t) });
      } finally {
        setBusy(false);
      }
    },
    [form, http, onToast, refreshPreview, reloadLimits, t]
  );

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void save();
  };

  const onEdit = (limit: CreditLimitView) => {
    setForm({
      subjectKind: limit.scope.kind === 'user' ? 'user' : 'group',
      subjectId:
        limit.scope.kind === 'group'
          ? limit.scope.groupId
          : limit.scope.kind === 'user'
            ? limit.scope.userId
            : '',
      amount: String(toCredits(limit.creditMillis)),
      cycle: limit.cycle,
      applyToTenant: limit.scope.kind === 'tenant',
      catalogModelId: limit.catalogModelId ?? '',
      overageBehavior: limit.overageBehavior,
      // Always sent: changing cycle or model derives a NEW id, and without this
      // the previous row would linger and keep binding.
      replaceId: limit.id
    });
  };

  const onDelete = async (limit: CreditLimitView) => {
    const confirmed = window.confirm(
      t('creditLimits.confirmDelete', {
        subject: subjectLabel(limit, nameOf, t('creditLimits.subjectTenant')),
        cycle: t(`creditLimits.cycle_${limit.cycle}`),
        model: limit.catalogModelId ?? t('creditLimits.modelAll'),
        amount: toCredits(limit.creditMillis)
      })
    );
    if (!confirmed) return;
    setBusy(true);
    try {
      await http.deleteCreditLimit(limit.id);
      onToast({ tone: 'success', message: t('creditLimits.deleted') });
      await Promise.all([reloadLimits(), refreshPreview()]);
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, t) });
    } finally {
      setBusy(false);
    }
  };

  const parentZero = pool !== undefined && !pool.unlimited && (pool.credit.remaining ?? 0) <= 0;
  const monthlyTenantWarning = limits.some(
    (limit) =>
      limit.scope.kind === 'tenant' &&
      limit.cycle === 'monthly' &&
      limit.consumedMillis !== undefined &&
      isQuotaWarningThresholdReached(limit.consumedMillis, limit.creditMillis)
  );
  const nextResetAt = limits.find((limit) => limit.cycle === form.cycle)?.resetAt;

  return (
    // `.screen`/`.usage-limits-screen` carry no CSS; content-stack is what every
    // other screen uses for the 16px gap between panels.
    <div className="screen usage-limits-screen content-stack">
      <section className="panel">
        <h1>{t('creditLimits.title')}</h1>
        <p className="muted">{t('creditLimits.subtitle')}</p>
        <p>
          {pool === undefined
            ? null
            : pool.unlimited
              ? t('creditLimits.summaryUnlimited', { consumed: toCredits(pool.credit.consumed) })
              : t('creditLimits.summary', {
                  granted: toCredits(pool.credit.granted ?? 0),
                  consumed: toCredits(pool.credit.consumed),
                  remaining: toCredits(pool.credit.remaining ?? 0)
                })}
        </p>
        {parentZero ? (
          <p className="warning" role="alert">
            {t('creditLimits.parentZero')}
          </p>
        ) : null}
        {monthlyTenantWarning ? (
          <p className="warning" role="alert">
            {t('creditLimits.monthlyTenantWarning')}
          </p>
        ) : null}
        <p className="muted">{t('creditLimits.budgetLink')}</p>
      </section>

      <TabStrip
        tabs={TABS.map((item) => ({ id: item.id, label: t(item.label) }))}
        value={tab}
        onChange={setTab}
        ariaLabel={t('creditLimits.title')}
        level="nested"
      />

      {/* Tabs stay mounted (hidden, not unmounted): a half-typed limit, the
          rate-missing warning, and an in-progress profile edit all have to
          survive a look at another tab. The wrappers carry no layout class —
          an author `display` rule beats the UA `[hidden]` rule, so a classed
          wrapper would stay visible (see .tab-strip[hidden] in tables.css) —
          so the inner div holds the stack spacing. */}
      <div hidden={tab !== 'limits'}>
        <div className="content-stack">
          <CreditLimitForm
            state={form}
            subjects={formSubjects}
            models={modelOptions}
            busy={busy}
            nextResetAt={nextResetAt}
            locale={locale}
            onChange={(next) => {
              setForm(next);
              setRateMissing(false);
            }}
            onSubmit={onSubmit}
            onCancelEdit={() => {
              setForm(EMPTY_FORM);
              setRateMissing(false);
            }}
            rateMissing={rateMissing}
            onSaveWithWarn={() => void save('warn')}
          />

          <CreditLimitTable
            limits={limits}
            advancedPolicyCount={advancedPolicyCount}
            locale={locale}
            busy={busy}
            nameOf={nameOf}
            onEdit={onEdit}
            onDelete={(limit) => void onDelete(limit)}
          />
        </div>
      </div>

      <div hidden={tab !== 'memberDefault'}>
        <MemberDefaultPanel
          http={http}
          groups={groups}
          users={users}
          tenantId={pool?.granteeId ?? ''}
          onToast={onToast}
        />
      </div>

      <div hidden={tab !== 'profiles'}>
        <div className="content-stack">
          <QuotaProfilesPanel
            http={http}
            models={modelOptions}
            onToast={onToast}
            onChanged={() => {
              setProfileRevision((revision) => revision + 1);
              void reloadProfiles();
            }}
          />

          <QuotaAssignmentReviewPanel
            http={http}
            profiles={profiles}
            users={users}
            reloadToken={profileRevision}
            onToast={onToast}
            onChanged={() => void refreshPreview()}
          />
        </div>
      </div>

      <div hidden={tab !== 'preview'}>
        <EffectiveLimitsPanel
          preview={preview}
          subjectKind={previewKind}
          subjectId={previewId}
          subjects={previewSubjects}
          models={modelOptions}
          catalogModelId={previewModel}
          locale={locale}
          busy={busy}
          onSubjectKindChange={(kind) => {
            setPreviewKind(kind);
            setPreviewId('');
          }}
          onSubjectIdChange={setPreviewId}
          onModelChange={setPreviewModel}
        />
      </div>
    </div>
  );
}
